import { Directive, HostListener } from '@angular/core';

// A click landing this soon after a touch was synthesized from that tap rather
// than produced by a mouse.
const TAP_CLICK_WINDOW_MS = 700;
// Two taps this close in time, and this close together on screen, are one
// double-tap. Held at the 300ms the ducharts cells use to delay a single click
// so the pair resolves before the action it is meant to replace.
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP_PX = 30;

/**
 * Fires `dblclick` for a double-tap on touch devices.
 *
 * iPadOS — and Chrome's device emulation — synthesize `click` from a tap but
 * never `dblclick`, so a chart that opens one thing on click and another on
 * double click silently loses the second action: it works with a mouse and is
 * dead on a tablet.
 *
 * Put this on an element wrapping the cells. It counts tap-derived clicks and
 * re-dispatches the second one as a `dblclick` on the element the tap landed on,
 * keeping the order a mouse produces — the cell's own click handler has already
 * run by the time the dblclick arrives, which is what lets a dblclick handler
 * cancel the pending single-click action instead of racing it. Mouse clicks are
 * left alone, since the browser already fires `dblclick` for those.
 *
 * The cells also need `touch-action: manipulation`, or Safari reserves the
 * second tap for zoom and holds the first click back while it waits to find out.
 */
@Directive({
  standalone: true,
  selector: '[touchDblclick]'
})
export class TouchDblclickDirective {

  private lastTouchAt = 0;
  private tapAt = 0;
  private tapX = 0;
  private tapY = 0;

  // Single-finger taps only: a pinch or a two-finger scroll ends with fingers
  // still down, and is not something to read as a click pair.
  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    this.lastTouchAt = event.touches.length ? 0 : Date.now();
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    const now = Date.now();

    // No recent touch — a mouse is driving and the browser handles dblclick.
    if (now - this.lastTouchAt > TAP_CLICK_WINDOW_MS) {
      this.tapAt = 0;
      return;
    }

    const isSecondTap = this.tapAt > 0
      && now - this.tapAt < DOUBLE_TAP_MS
      && Math.abs(event.clientX - this.tapX) < DOUBLE_TAP_SLOP_PX
      && Math.abs(event.clientY - this.tapY) < DOUBLE_TAP_SLOP_PX;

    if (!isSecondTap) {
      this.tapAt = now;
      this.tapX = event.clientX;
      this.tapY = event.clientY;
      return;
    }

    // A third tap starts a fresh pair instead of firing a second dblclick.
    this.tapAt = 0;

    (event.target as HTMLElement).dispatchEvent(new MouseEvent('dblclick', {
      bubbles: true,
      cancelable: true,
      view: window,
      detail: 2,
      clientX: event.clientX,
      clientY: event.clientY,
      screenX: event.screenX,
      screenY: event.screenY,
    }));
  }
}
